import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Empty,
  Space,
  Grid,
  Input,
  Pagination,
} from "antd";
import { BoxAdd, Box, Edit, Trash } from "iconsax-react";
import { deleteProduct, getProductById } from "./adminApi";
import ProductModal from "./ProductModal";
import Navbar from "../../../components/Navbar";
import { fetchProducts } from "../../Home/api";
import dayjs from "dayjs";
import "./AdminPage.css";

const { useBreakpoint } = Grid;

const CATEGORY_MAP = {
  CO1: "Chemical",
  C02: "Laboratory Equipment",
  C03: "Mining Equipment",
};

const AdminPage = () => {
  const [products, setProducts] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const screens = useBreakpoint();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data = [] } = await fetchProducts();
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = async (id) => {
    const { data } = await getProductById(id);
    setEditProduct(data);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    await deleteProduct(id);
    message.success("Product deleted");
    fetchData();
  };

  const columns = [
    {
      title: "No",
      key: "index",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1 + ".",
    },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Brand", dataIndex: "brand", key: "brand" },
    {
      title: "Thumbnail",
      dataIndex: "thumbnail",
      key: "thumbnail",
      render: (url) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <img
            src={url}
            alt="thumbnail"
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        </div>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => dayjs(value).format("DD MMM YYYY, HH:mm"),
    },
    {
      title: "Category",
      dataIndex: "category_code",
      key: "category_code",
      render: (value) => CATEGORY_MAP[value] || value,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handleEdit(record.id)}
            icon={<Edit size="18" color="#1677ff" />}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<Trash size="18" color="#ff4d4f" />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ProductCardList = () => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    const paginatedProducts = products.slice(start, end);

    return (
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-3 max-w-6xl mx-auto">
        {paginatedProducts.length === 0 ? (
          <div className="col-span-full text-center text-lg font-medium text-gray-500">
            Products Not Available...
          </div>
        ) : (
          paginatedProducts.map((item) => (
            <div
              key={item.id}
              className="product-card p-4 flex flex-col"
              onClick={() => handleEdit(item.id)}
            >
              <img
                src={item.thumbnail}
                alt={item.title}
                className="product-image mb-4 mx-auto"
              />
              <h2 className="text-lg font-semibold mb-2">{item.title}</h2>
              <Input.TextArea
                value={item.description}
                readOnly
                autoSize
                className="!text-sm !text-[#5c4033] !bg-transparent !border-none !p-0"
              />
              <div className="mt-4 flex justify-between items-center text-sm font-bold text-[#5c4033]">
                <span></span>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    type="primary"
                    icon={<Edit size={16} color="#fff" />}
                    onClick={() => handleEdit(item.id)}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this product?"
                    onConfirm={() => handleDelete(item.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      size="small"
                      danger
                      icon={<Trash size={16} color="#ff0000" />}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <>
      <Navbar admin />
      <div className="admin-page p-4 pt-28">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Box size={24} variant="Bold" color="#1677ff" />
            {screens.md ? "Product Management" : "Product"}
          </h1>
          <Button
            type="primary"
            icon={<BoxAdd size={18} color="#fff" />}
            onClick={() => {
              setEditProduct(null);
              setOpenModal(true);
            }}
          >
            Add Product
          </Button>
        </div>

        {screens.md ? (
          <Table
            size="small"
            columns={columns}
            dataSource={products}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showQuickJumper: false,
              total: products.length,
            }}
            onChange={(pagination) => {
              setPagination((prev) => ({
                current:
                  pagination.pageSize !== prev.pageSize
                    ? 1
                    : pagination.current,
                pageSize: pagination.pageSize,
              }));
            }}
            className="mt-4"
            locale={{
              emptyText: <Empty description="Products Not Available" />,
            }}
          />
        ) : (
          <>
            <ProductCardList />
            <div className="flex justify-center mt-6 md:hidden">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={products.length}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    current: page,
                    pageSize:
                      pageSize !== prev.pageSize ? pageSize : prev.pageSize,
                  }));
                }}
                showSizeChanger
                pageSizeOptions={["5", "10", "20", "50"]}
              />
            </div>
            <div className="block md:hidden min-h-[300px]"></div>
          </>
        )}

        <ProductModal
          visible={openModal}
          onClose={() => setOpenModal(false)}
          product={editProduct}
          refreshData={fetchData}
        />
      </div>
    </>
  );
};

export default AdminPage;
